import { useMemo, useState } from 'react'
import { FlatList, Pressable, Text, View } from 'react-native'
import { useQuery } from '@apollo/client'
import { type Href, useGlobalSearchParams, useRouter } from 'expo-router'
import { useTranslation } from 'react-i18next'
import { SheetManager } from 'react-native-actions-sheet'

import { PAGE_SIZE } from '@/constants/common'
import { GET_JBP_DEPOSIT_INVOICES } from '@/constants/graphql/queries'
import type { Mood, OrderListParams } from '@/types/common'
import { cn } from '@/utils/cn'
import { formatDate } from '@/utils/formatter'
import { formatJbpCurrency, type JbpDepositInvoice } from '@/utils/jbp'
import { Tag } from '@/components/base'
import { ExpandMore, ReceiptOff, Spinner } from '@/components/icon'
import RefreshControl from '@/components/RefreshControl'

type FastTrackOrderListProps = {
  status: 'open' | 'closed'
}

type JbpDepositInvoicesQuery = {
  jbpDepositInvoicesConnection: {
    edges: Array<{ cursor: string; node: JbpDepositInvoice }>
    pageInfo: { hasPreviousPage: boolean; startCursor?: string | null }
  }
}

const OPEN_STATES = new Set(['CREATING', 'AWAIT_SETTLEMENT', 'ADMIN_RESOLVING'])
const CLOSED_STATES = new Set(['COMPLETED', 'MANUALLY_COMPLETED', 'ADMIN_RESOLVED', 'FAILED', 'TIMED_OUT'])

export default function FastTrackOrderList({ status }: FastTrackOrderListProps) {
  const { filter = 'all' } = useGlobalSearchParams<OrderListParams>()
  const [isRefreshing, setIsRefreshing] = useState(false)
  const [isFetchingMore, setIsFetchingMore] = useState(false)
  const { data, loading, fetchMore, refetch } = useQuery<JbpDepositInvoicesQuery>(GET_JBP_DEPOSIT_INVOICES, {
    variables: { last: PAGE_SIZE }
  })

  const connection = data?.jbpDepositInvoicesConnection
  const invoices = connection?.edges.toReversed().map((edge) => edge.node) ?? []
  const visibleInvoices = useMemo(
    () => invoices.filter((invoice) => matchesStatus(invoice.state, status, filter)),
    [filter, invoices, status]
  )

  async function handleRefetch() {
    setIsRefreshing(true)
    try {
      await refetch({ last: PAGE_SIZE })
    } finally {
      setIsRefreshing(false)
    }
  }

  async function handleFetchMore() {
    if (!connection?.pageInfo.hasPreviousPage || isFetchingMore) {
      return
    }

    setIsFetchingMore(true)
    try {
      await fetchMore({
        variables: { last: PAGE_SIZE, before: connection.pageInfo.startCursor },
        updateQuery: (previous, { fetchMoreResult }) => {
          if (!fetchMoreResult) {
            return previous
          }

          const seenInvoiceIds = new Set(
            previous.jbpDepositInvoicesConnection.edges.map((edge) => edge.node.invoiceId)
          )
          return {
            jbpDepositInvoicesConnection: {
              edges: [
                ...fetchMoreResult.jbpDepositInvoicesConnection.edges.filter(
                  (edge) => !seenInvoiceIds.has(edge.node.invoiceId)
                ),
                ...previous.jbpDepositInvoicesConnection.edges
              ],
              pageInfo: {
                ...previous.jbpDepositInvoicesConnection.pageInfo,
                hasPreviousPage: fetchMoreResult.jbpDepositInvoicesConnection.pageInfo.hasPreviousPage,
                startCursor:
                  fetchMoreResult.jbpDepositInvoicesConnection.pageInfo.startCursor ??
                  previous.jbpDepositInvoicesConnection.pageInfo.startCursor
              }
            }
          }
        }
      })
    } finally {
      setIsFetchingMore(false)
    }
  }

  if (loading && !data) {
    return (
      <View className={'h-full items-center justify-center bg-background-secondary'}>
        <View className={'animate-spin'}>
          <Spinner className={'size-6 text-primary'} />
        </View>
      </View>
    )
  }

  return (
    <FlatList
      contentContainerStyle={{ flexGrow: 1, padding: 16, gap: 16 }}
      data={visibleInvoices}
      keyExtractor={(invoice) => invoice.invoiceId}
      onEndReached={handleFetchMore}
      refreshControl={<RefreshControl refreshing={isRefreshing} onRefresh={handleRefetch} />}
      renderItem={({ item: invoice, index }) => (
        <>
          <FastTrackOrderCard invoice={invoice} />
          {isFetchingMore && index === visibleInvoices.length - 1 && (
            <View className={'mx-auto mt-4 animate-spin'}>
              <Spinner className={'size-6 text-primary'} />
            </View>
          )}
        </>
      )}
      ListHeaderComponent={<ListHeader status={status} />}
      ListEmptyComponent={<EmptyList />}
    />
  )
}

function FastTrackOrderCard({ invoice }: { invoice: JbpDepositInvoice }) {
  const router = useRouter()
  const { t } = useTranslation()
  const state = getStateDetails(invoice.state)
  const hasActualPaidAmount = Number(invoice.paidAmount) > 0

  return (
    <Pressable
      className={'rounded-lg border border-border-10 bg-background-primary p-4'}
      onPress={() => router.push(`/order-list/jbp/${invoice.invoiceId}` as Href)}
    >
      <View className={'flex-row items-start justify-between'}>
        <View className={'gap-0.5'}>
          <Text className={'text-base font-semibold text-text-primary'}>{t('orders:fastTrack')}</Text>
          <Text className={'font-roboto-medium text-xs text-text-tertiary'}>{formatDate(invoice.createdAt)}</Text>
        </View>
        <Tag mood={state.mood}>{t(`orderList:fastTrack.${state.translationKey}`)}</Tag>
      </View>
      <View className={'h-8'} />
      <View className={'flex-row justify-between'}>
        <Text className={'text-sm font-medium text-text-tertiary'}>{t('orders:requestedAmount')}</Text>
        <Text className={'font-roboto-medium text-sm font-medium text-primary'}>
          {formatJbpCurrency(invoice.requestedAmount)}
        </Text>
      </View>
      {hasActualPaidAmount && (
        <View className={'mt-2 flex-row justify-between'}>
          <Text className={'text-sm font-medium text-text-tertiary'}>{t('orders:actualPaidAmount')}</Text>
          <Text className={'font-roboto-medium text-sm font-medium text-primary'}>
            {formatJbpCurrency(invoice.paidAmount)}
          </Text>
        </View>
      )}
      <View className={'h-4'} />
      <View className={'h-10 items-center justify-center rounded-lg bg-primary-subtle'}>
        <Text className={'font-sm font-medium text-primary'}>{t('orders:viewOrder')}</Text>
      </View>
    </Pressable>
  )
}

function ListHeader({ status }: FastTrackOrderListProps) {
  const { t } = useTranslation()
  const router = useRouter()
  const { filter = 'all' } = useGlobalSearchParams<OrderListParams>()
  const [isDrawerOpen, setIsDrawerOpen] = useState(false)

  return (
    <View className={'flex-row'}>
      <Pressable
        className={cn(
          'my-[-8px] flex-row items-center gap-x-1 rounded-lg px-2 py-1.5',
          isDrawerOpen && 'bg-fill-subtle'
        )}
        onPress={() => {
          setIsDrawerOpen(true)
          SheetManager.show('open-filter', {
            payload: {
              type: `${status}-fast-track-order`,
              value: filter,
              setValue: (nextFilter) => router.setParams({ filter: nextFilter }),
              closeDrawer: () => setIsDrawerOpen(false)
            }
          })
        }}
      >
        <Text className={'text-xs font-medium text-text-secondary'}>
          {t('orders:status', { status: t(`orderList:fastTrack.${filter}`) })}
        </Text>
        <ExpandMore className={'size-4 text-text-secondary'} />
      </Pressable>
      <View className={'flex-1'} />
    </View>
  )
}

function EmptyList() {
  const { t } = useTranslation()
  return (
    <View className={'mb-[-16px] flex-1 items-center justify-center'}>
      <View className={'items-center gap-4'}>
        <ReceiptOff className={'size-20 text-text-quaternary'} />
        <Text className={'text-sm text-text-secondary'}>{t('orders:noOrders')}</Text>
      </View>
    </View>
  )
}

function matchesStatus(state: string, status: FastTrackOrderListProps['status'], filter: string) {
  const stateMatchesLifecycle = (status === 'open' ? OPEN_STATES : CLOSED_STATES).has(state)
  return stateMatchesLifecycle && (filter === 'all' || getStateDetails(state).translationKey === filter)
}

function getStateDetails(state: string): { mood: Mood; translationKey: string } {
  switch (state) {
    case 'CREATING':
      return { mood: 'info', translationKey: 'creating' }
    case 'AWAIT_SETTLEMENT':
      return { mood: 'warning', translationKey: 'awaitSettlement' }
    case 'ADMIN_RESOLVING':
      return { mood: 'warning', translationKey: 'adminResolving' }
    case 'COMPLETED':
    case 'MANUALLY_COMPLETED':
    case 'ADMIN_RESOLVED':
      return { mood: 'success', translationKey: 'completed' }
    case 'FAILED':
      return { mood: 'error', translationKey: 'failed' }
    case 'TIMED_OUT':
      return { mood: 'error', translationKey: 'timedOut' }
    default:
      return { mood: 'neutral', translationKey: 'all' }
  }
}
